import { useState } from "react";
import { createPortal } from "react-dom";
import { Phone, RotateCcw, Users, CalendarClock, Mail, MessagesSquare, ChevronDown, ChevronRight } from "lucide-react";
import { useTranslation } from "../i18n";
import { pname } from "../utils";
import { choosableCo } from "../lib/collaboratorState";
import { InitialsBadge, ScrollHint, useScrollHint } from "./SharedUI";
import { RAD, FS, Z, POPUP_PAD } from "../lib/tokens"; // 204 §5 — the inset the bell shares
import { useConfirm } from "./useConfirmAndUndo";

/* Phase 31 (C2) → Phase 39 Corr — the phone-icon "Internal communication"
   panel. Two sections plus the call log:
   (1) DIRECT CALLS (max. 4) — every callable colleague as a pill in two
       alphabetical columns, each with a /api/presence dot. Click an ONLINE
       colleague to ring them immediately (1-to-1; more get added inside the
       call window); click an OFFLINE colleague to confirm + log a missed call
       on their side via the no-ring path. The pane collapses while/after a
       call so the pop-out stays uncluttered.
   (2) MEETINGS AND EMAIL — the three Parties-pane actions (schedule a meeting,
       schedule a poll, send an email) re-pointed at the firm roster with no
       case attached; the parent owns the modals.
   (3) the CALL LOG — initiator + invitee chips, outcome, and callback / retry
       buttons (enlarged for easier clicking).
   Rendered as a fixed portal anchored to the trigger rect, OR inline as a
   chrome-less block inside the compact column. The parent owns all data +
   actions. */

const OUTCOME_COLOR: Record<string, string> = {
  answered: "var(--gn)",
  declined: "var(--am)",
  missed: "var(--rd)",
  ringing: "var(--ac)",
  /* 31.1 (item 6) — caller-side abort: the CALL status only (participants
     are flipped to missed — the invitee's row reads like a missed call). */
  canceled: "var(--tm)",
};

function fmtCallTs(iso: any) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm} ${hh}:${mi}`;
}

export default function CallsPanel({
  anchorRect,
  onClose,
  co = [],
  currentUser,
  presenceIds = [],
  /* Corr (item 7) — true once the first /presence pull has resolved. Before
     that the dots are unknown, so a click awaits a fresh pull rather than
     misclassifying everyone as offline. */
  presenceLoaded = false,
  onEnsurePresence,
  calls = [],
  onCall,
  /* Corr (D1) — write a missed-call row on the colleague's side without
     ringing / acquiring media (used when an OFFLINE colleague is clicked). */
  onNotifyMissed,
  busy = false,
  /* Received for API compatibility with the callers (a shared prop also used by
     the CallAvatar indicators elsewhere). Phase 42: no longer consumed here — the
     old auto-collapse-on-call was removed; the Direct calls section never
     collapses on its own. */
  callActive = false,
  /* Corr (Section 2) — open the three case-free modals (parent-owned). */
  onScheduleMeeting,
  onSchedulePoll,
  onSendEmail,
  /* Corr-2 (item 1) — the General chat subsection content, supplied by the host
     (App's header dropdown / the compact column) already wired to its chat
     state. Rendered as a collapsible subsection inside COMMS when present. */
  chatSlot,
  /* Corr-2 (item 4) — inline host mode: no portal, no backdrop, no fixed
     anchoring; the panel renders as a chrome-less block inside the compact
     column (the bell / Eisenhower inline-panel pattern), sized by its host. */
  inline = false,
  /* 209 §27 — « the phone view fills the height it has »: the column's phone view
     passes it, and the call log and the general chat take the room the window
     gives them (1 : 2) and scroll beyond it, instead of windows about four rows
     tall. Absent — the header's popup — nothing changes. */
  fill = false,
}: any) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  const meId = Number(currentUser);
  const online = new Set((presenceIds || []).map(Number));
  const byId = new Map(co.map((c: any) => [c.id, c]));

  /* 31.1 (item 2) — ONE normalized identity for every id list leaving here.
     The server filters self/NaN ids out and 400s an emptied list, so any
     identity-shape drift silently produced the prod []-initiate; cleanIds()
     is the single gate. Still used by the call-log callback / retry buttons. */
  const cleanIds = (ids: any) => [
    ...new Set(
      (ids || [])
        .map(Number)
        .filter((n: any) => Number.isFinite(n) && n > 0 && n !== meId),
    ),
  ];

  /* Corr (item 4) — one alphabetical roster, laid out in two columns. External
     collaborators keep the dashed-border cue. */
  const byName = (a: any, b: any) =>
    String(pname(a)).localeCompare(String(pname(b)), undefined, {
      sensitivity: "base",
    });
  // 75 §4 — ringing somebody is choosing them for new work: archived people
  // leave the list (suspended stay — they are simply offline until reinstated).
  const callable = choosableCo(co).filter((c: any) => Number(c.id) !== meId).sort(byName);

  /* Corr-2 (items 4,6,7) — per-subsection collapse. Default OPEN (a subsection
     is collapsed only once its id is set true); CallsPanel unmounts when the
     panel closes, so this local state resets to all-open on every reopen — the
     "persist only for the life of the open window" rule. Phase 42: the Direct
     calls section NEVER auto-collapses — the old call-launch auto-collapse was
     removed; only the manual chevron toggles it. */
  const [collapsed, setCollapsed] = useState<Record<string, any>>({});
  const isOpen = (id: any) => !collapsed[id];
  const toggleSub = (id: any) =>
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  /* Corr (items 5–7) — a single click decides: online → ring now; offline →
     confirm, then log a missed call on their side. If presence hasn't resolved
     yet (the blank window right after opening), await one fresh pull first so
     an online colleague is never misread as offline. */
  const clickColleague = async (c: any) => {
    if (busy) return;
    let onlineSet = online;
    if (!presenceLoaded && onEnsurePresence) {
      try {
        const ids = await onEnsurePresence();
        if (Array.isArray(ids)) onlineSet = new Set(ids.map(Number));
      } catch {
        /* keep the last-known set */
      }
    }
    if (onlineSet.has(Number(c.id))) {
      onCall && onCall([Number(c.id)]);
    } else {
      askConfirm({
        // 161C §3 — shares CallWindow.tsx:942's key; one question, one title.
        title: t("confirm.notify_missed_call_title", {
          name: pname(c),
          fallback: `Notify ${pname(c)}?`,
        }),
        message: t("calls.offline_confirm", {
          name: pname(c),
          fallback: `${pname(c)} is offline, notify them you tried to call?`,
        }),
        onConfirm: () => onNotifyMissed && onNotifyMissed(Number(c.id)),
      });
    }
  };

  /* ── 151E §1(a) — THE PANEL NEVER OUTGROWS THE VIEWPORT, measured first:
     under the root CSS zoom (1.2307 here) the old `maxHeight: 82vh` rendered
     82vh × zoom ≈ 101vh VISUAL and the anchor's visual-px `bottom` was
     re-scaled as layout px, so the panel stood 75→882 in an 800px window —
     82px off the bottom (the screenshots). The 142 §1 rule: convert the
     WHOLE expression — rect reads are visual, CSS px are layout, so every
     coordinate divides by the root zoom once, and the cap is the layout
     viewport minus the panel's own top minus a 12px margin. The content
     scrolls inside; the 151D §2(d) hint discs say so. */
  const _zoom =
    parseFloat(String(getComputedStyle(document.documentElement).zoom || "1")) || 1;
  const top = ((anchorRect ? anchorRect.bottom : 60) + 8) / _zoom;
  const right = Math.max(
    8,
    (window.innerWidth - (anchorRect ? anchorRect.right : window.innerWidth - 20)) / _zoom,
  );
  const maxH = Math.max(200, window.innerHeight / _zoom - top - 12);
  const hint = useScrollHint();

  const outcomeLabel = (s: any) =>
    s === "answered"
      ? t("calls.outcome.answered", { fallback: "answered" })
      : s === "declined"
        ? t("calls.outcome.declined", { fallback: "declined" })
        : s === "missed"
          ? t("calls.outcome.missed", { fallback: "missed" })
          : s === "canceled"
            ? t("calls.outcome.canceled", { fallback: "canceled" })
            : t("calls.outcome.ringing", { fallback: "ringing…" });

  const sectionTitleStyle = {
    fontSize: FS.s10_5,
    fontWeight: 700,
    color: "var(--ts)",
    letterSpacing: ".2px",
  };

  /* Corr-2 (items 2,4) — one subsection header: title (with an optional icon
     AFTER it) on the left, the collapse chevron FAR RIGHT. Plain render helper
     (not a component) so it has no independent identity. */
  const subHeader = (id: any, title: any, iconAfter?: any) => (
    <button
      type="button"
      onClick={() => toggleSub(id)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: 0,
        background: "none",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        ...sectionTitleStyle,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, minWidth: 0 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </span>
        {iconAfter}
      </span>
      {isOpen(id) ? (
        <ChevronDown size={13} style={{ color: "var(--tm)", flexShrink: 0 }} />
      ) : (
        <ChevronRight size={13} style={{ color: "var(--tm)", flexShrink: 0 }} />
      )}
    </button>
  );

  /* ── Section 1: direct calls (two-column alphabetical pills) ─────────────── */
  const directCalls = (
    <div style={{ marginBottom: 10 }}>
      {subHeader("direct", t("calls.section.direct", { fallback: "Direct calls" }))}
      {isOpen("direct") && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 4,
            marginTop: 6,
          }}
        >
          {callable.length === 0 && (
            <span style={{ fontSize: FS.s10, color: "var(--tm)", gridColumn: "1 / -1" }}>
              {t("calls.no_colleagues", { fallback: "No colleagues to call" })}
            </span>
          )}
          {callable.map((c: any) => {
            const isOn = online.has(Number(c.id));
            return (
              <button
                key={c.id}
                type="button"
                disabled={busy}
                onClick={() => clickColleague(c)}
                title={
                  isOn
                    ? t("calls.presence_online_click", {
                        fallback: "Online — click to call",
                      })
                    : t("calls.presence_offline_click", {
                        fallback: "Offline — click to log a missed call",
                      })
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  width: "100%",
                  minWidth: 0,
                  padding: "4px 8px",
                  fontSize: FS.s10_5,
                  fontFamily: "inherit",
                  background: "var(--bi)",
                  color: "var(--ts)",
                  border:
                    c.coType === "external"
                      ? "1px dashed var(--br)"
                      : "1px solid var(--br)",
                  borderRadius: RAD.r8,
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.6 : 1,
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: RAD.pill,
                    background: isOn ? "var(--gn)" : "var(--tm)",
                    opacity: isOn ? 1 : 0.45,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {pname(c)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  /* ── Section 2: meetings and email (the three Parties-pane actions) ──────── */
  const s2Btn = (bg: any, brd: any, fg: any, icon: any, label: any, onClick: any) => (
    <button
      type="button"
      className="btn"
      onClick={onClick}
      title={label}
      style={{
        flex: 1,
        justifyContent: "center",
        padding: "6px 8px",
        background: bg,
        color: fg,
        border: `1px solid ${brd}`,
        borderRadius: RAD.r8,
      }}
    >
      {icon}
    </button>
  );
  const meetingsEmail = (
    <div style={{ marginBottom: 10 }}>
      {subHeader("meet", t("calls.section.meetings", { fallback: "Meetings and email" }))}
      {isOpen("meet") && (
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        {s2Btn(
          "rgba(74,124,255,.15)",
          "rgba(74,124,255,.3)",
          "var(--ac)",
          <Users size={16} color="var(--tp)" />,
          t("modal.meeting.title", { fallback: "Schedule a meeting" }),
          () => onScheduleMeeting && onScheduleMeeting(),
        )}
        {s2Btn(
          "rgba(167,139,250,.15)",
          "rgba(167,139,250,.3)",
          "var(--pp)",
          <CalendarClock size={16} color="var(--tp)" />,
          t("modal.poll.title", { fallback: "Schedule a poll" }),
          () => onSchedulePoll && onSchedulePoll(),
        )}
        {s2Btn(
          "rgba(74,222,128,.15)",
          "rgba(74,222,128,.3)",
          "var(--gn)",
          <Mail size={16} color="var(--tp)" />,
          t("calls.email_colleagues", { fallback: "Email colleagues" }),
          () => onSendEmail && onSendEmail(),
        )}
      </div>
      )}
    </div>
  );

  /* ── General chat subsection (item 1/2/3) — host-supplied content; icon
     AFTER the (sentence-case) title; collapse chevron far right. */
  const generalChat = chatSlot ? (
    <div style={{ marginBottom: 10, ...(fill && isOpen("gchat") ? FILL_BOX_CHAT : {}) }}>
      {subHeader(
        "gchat",
        t("calls.section.general_chat", { fallback: "General chat" }),
        <MessagesSquare size={12} style={{ color: "var(--tm)" }} />,
      )}
      {/* Corr-4 (item 3) — indent the chat to the panel content inset (was
          flush-left). */}
      {isOpen("gchat") && (
        <div style={{ marginTop: 6, paddingLeft: 10, ...(fill ? FILL_COLUMN : {}) }}>{chatSlot}</div>
      )}
    </div>
  ) : null;

  /* ── Call log subsection (items 4,7) — sentence-case title, own chevron; a
     ~4-row visible window that SCROLLS to reveal the full log (no hard cap).
     The missed-call badge counts the full set regardless. */
  const callLog = (
    <div style={{ marginBottom: 10, ...(fill && isOpen("log") ? FILL_BOX_LOG : {}) }}>
      {subHeader("log", t("calls.log_title", { fallback: "Call log" }))}
      {isOpen("log") && (
        <div style={{ marginTop: 4, ...(fill ? FILL_SCROLL : { maxHeight: 132 }), overflowY: "auto" }}>
          {calls.length === 0 && (
            <p
              style={{
                fontSize: FS.s10_5,
                color: "var(--tm)",
                textAlign: "center",
                padding: "10px 8px",
                margin: 0,
              }}
            >
              {t("calls.empty_log", { fallback: "No calls yet" })}
            </p>
          )}
          {[...calls]
            .sort((a, b) =>
              String(b.startedAt || "").localeCompare(String(a.startedAt || "")),
            )
            .map((call) => renderCallRow(call))}
        </div>
      )}
    </div>
  );

  const body = (
    <>
      {/* Corr-2 (item 5) — section title in CAPS to match TASKS / TIMER /
          AGENDA, at the standard panel-title size (13px); thinned divider. */}
      <div
        style={{
          fontSize: FS.s13,
          fontWeight: 600,
          color: "var(--tp)",
          textTransform: "uppercase",
          letterSpacing: ".3px",
          paddingBottom: 6,
          marginBottom: 8,
          borderBottom: "1px solid rgba(255,255,255,.06)",
        }}
      >
        {t("calls.panel_title", { fallback: "Internal communication" })}
      </div>

      {/* Corr-2 (item 6) — COMMS subsection order. */}
      {directCalls}
      {callLog}
      {generalChat}
      {(onScheduleMeeting || onSchedulePoll || onSendEmail) && meetingsEmail}
    </>
  );

  function renderCallRow(call: any) {
    const initiator = byId.get(call.initiatorId);
          /* 31.1 (item 2) — normalized identity + cleaned id lists: a quick
             action can never post a list the server folds to []. */
          const mine = Number(call.initiatorId) === meId;
          const callbackIds = cleanIds([call.initiatorId]);
          const retryIds = cleanIds(
            call.participants.map((p: any) => p.collaboratorId),
          );
          const others = cleanIds([
            call.initiatorId,
            ...call.participants.map((p: any) => p.collaboratorId),
          ]);
          const myPart = call.participants.find(
            (p: any) => Number(p.collaboratorId) === meId,
          );
          const missedForMe = myPart && myPart.outcome === "missed";
          const declinedWithTime = call.participants.filter(
            (p: any) => p.outcome === "declined" && p.availableFrom,
          );
          return (
            <div
              key={call.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 4px",
                borderBottom: "1px solid rgba(255,255,255,.04)",
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: FS.s9_5,
                  color: "var(--tm)",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {fmtCallTs(call.startedAt)}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  flexShrink: 0,
                }}
                title={
                  initiator
                    ? t("calls.initiated_by", {
                        name: pname(initiator),
                        fallback: `Initiated by ${pname(initiator)}`,
                      })
                    : ""
                }
              >
                {initiator && (
                  <InitialsBadge
                    person={initiator}
                    isMe={mine}
                    size={18}
                    fontSize={8}
                    {...({} as any)}
                  />
                )}
                {call.isCollective && (
                  <Users size={11} style={{ color: "var(--tm)" }} />
                )}
                <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>→</span>
                {call.participants.map((p: any) => {
                  const c = byId.get(p.collaboratorId);
                  if (!c) return null;
                  return (
                    <InitialsBadge
                      key={p.collaboratorId}
                      person={c}
                      isMe={Number(p.collaboratorId) === meId}
                      size={18}
                      fontSize={8}
                      title={`${pname(c)} — ${outcomeLabel(p.outcome)}${p.availableFrom ? ` (${p.availableFrom})` : ""}`}
                      style={{
                        boxShadow: `0 0 0 1.5px ${OUTCOME_COLOR[p.outcome] || "var(--br)"}`,
                      }}
                    />
                  );
                })}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    fontSize: FS.s9_5,
                    fontWeight: 600,
                    color: OUTCOME_COLOR[call.status] || "var(--ts)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {missedForMe
                    ? t("calls.outcome.missed", { fallback: "missed" })
                    : outcomeLabel(call.status)}
                </span>
                {/* Corr-2 (item 3) — availability time only; the decliner's
                    name lives in the tooltip (and on their initials chip). */}
                {declinedWithTime.length > 0 && (
                  <span
                    style={{
                      fontSize: FS.s9,
                      color: "var(--am)",
                      marginLeft: 5,
                      whiteSpace: "nowrap",
                    }}
                    title={
                      t("calls.available_from_title", {
                        fallback: "Declined — available from",
                      }) +
                      " — " +
                      declinedWithTime
                        .map(
                          (p: any) =>
                            `${byId.get(p.collaboratorId) ? pname(byId.get(p.collaboratorId)) : "?"} ${p.availableFrom}`,
                        )
                        .join(" · ")
                    }
                  >
                    {declinedWithTime.map((p: any) => `⏲ ${p.availableFrom}`).join(" · ")}
                  </span>
                )}
              </span>
              {/* Corr (item: enlarge call-again icons) — roughly the
                  initials-badge size for an easier click target. */}
              <span
                style={{
                  display: "inline-flex",
                  gap: 4,
                  flexShrink: 0,
                  alignItems: "center",
                }}
              >
                {!mine && callbackIds.length > 0 && (
                  <button
                    type="button"
                    className="emRowAct"
                    disabled={busy}
                    style={callAgainBtnStyle}
                    onClick={() => onCall(callbackIds)}
                    title={t("calls.callback_title", {
                      name: initiator ? pname(initiator) : "",
                      fallback: `Call back ${initiator ? pname(initiator) : ""}`,
                    })}
                  >
                    <Phone size={17} />
                  </button>
                )}
                {call.isCollective && !mine && others.length > 0 && (
                  <button
                    type="button"
                    className="emRowAct"
                    disabled={busy}
                    style={callAgainBtnStyle}
                    onClick={() => onCall(others)}
                    title={t("calls.call_all_title", {
                      fallback: "Call everyone on this call",
                    })}
                  >
                    <Users size={17} />
                  </button>
                )}
                {mine && retryIds.length > 0 && (
                  <button
                    type="button"
                    className="emRowAct"
                    disabled={busy}
                    style={callAgainBtnStyle}
                    onClick={() => onCall(retryIds)}
                    title={t("calls.retry_title", {
                      fallback: "Retry this call",
                    })}
                  >
                    <RotateCcw size={17} />
                  </button>
                )}
              </span>
            </div>
          );
  }

  if (inline) {
    return (
      <div
        ref={hint.ref}
        style={{
          /* Corr-3 (item 6) — taller so the Meetings-and-email buttons aren't
             clipped; subsections each scroll within their own window.
             151E §1(a) — the bare vh was VISUAL under the host document's
             zoom (the same 82vh→101vh arithmetic as the portal arm); the
             app's own layout-viewport idiom bounds it for real.
             209 §27 — filling, the panel IS the column's view: its height is
             the column body's, and the log and the chat share what is left. */
          ...(fill ? FILL_PANEL : { maxHeight: "calc(78dvh / var(--ui-zoom, 1))" }),
          overflowY: "auto",
          padding: POPUP_PAD, // 204 §5 — this panel's own figure, named (tokens.ts)
          borderBottom: "1px solid var(--br)",
        }}
      >
        {hint.above && <ScrollHint dir="up" />}
        {body}
        {hint.below && <ScrollHint dir="down" />}
      </div>
    );
  }

  return createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: Z.topBackdrop }}
        onClick={onClose}
      />
      <div
        ref={hint.ref}
        style={{
          position: "fixed",
          top,
          right,
          width: 360,
          /* 151E §1(a) — bounded to the layout viewport (see the block at
             `top` above); the content scrolls within, the discs say so. */
          maxHeight: maxH,
          overflowY: "auto",
          background: "var(--bc)",
          border: "1px solid var(--br)",
          borderRadius: RAD.r10,
          boxShadow: "0 8px 28px rgba(0,0,0,.7)",
          zIndex: Z.topMenu,
          padding: POPUP_PAD, // 204 §5 — this panel's own figure, named (tokens.ts)
        }}
      >
        {hint.above && <ScrollHint dir="up" />}
        {body}
        {hint.below && <ScrollHint dir="down" />}
      </div>
    </>,
    document.body,
  );
}

/* 209 §27 — the fill mode's boxes: the panel a flex column the height of the
   column's body; the call log and the general chat each a share of what is
   left (1 : 2, never below a few rows), scrolling within. */
const FILL_PANEL = { flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" } as const;
const FILL_BOX_LOG = { flex: "1 1 0", minHeight: 90, display: "flex", flexDirection: "column" } as const;
const FILL_BOX_CHAT = { flex: "2 1 0", minHeight: 150, display: "flex", flexDirection: "column" } as const;
const FILL_SCROLL = { flex: "1 1 auto", minHeight: 0 } as const;
const FILL_COLUMN = { flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" } as const;

/* Corr — enlarged call-again target (~initials-badge size). emRowAct keeps the
   hover/focus styling; these dims just grow the hit area + glyph. */
const callAgainBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  padding: 0,
};
